import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { createNewsletterWithCarryForward, fetchNewsletters } from '@/store/slices/newslettersSlice';
import { ROUTES } from '@/constants/routes';
import { EmptyState, ErrorState, LoadingState, ConfirmDialog } from '@/components/common/Common';
import NewsletterCard from '@/components/newsletter/NewsletterCard';
import { useNewsletterDelete } from '@/hooks/useNewsletterDelete';

type FilterTab = 'all' | 'draft' | 'published';

export default function NewsletterListPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { items, status, error } = useAppSelector((state) => state.newsletters);
  const [tab, setTab] = useState<FilterTab>('all');
  const { pendingDelete, requestDelete, confirmDelete, cancelDelete } = useNewsletterDelete();

  useEffect(() => {
    dispatch(fetchNewsletters());
  }, [dispatch]);

  const handleCreate = async () => {
    const result = await dispatch(createNewsletterWithCarryForward()).unwrap();
    navigate(ROUTES.newsletterEdit(result.newsletter.id));
  };

  if (status === 'loading' || status === 'idle') return <LoadingState label="Loading newsletters…" />;
  if (status === 'failed') return <ErrorState message={error ?? 'Failed to load'} onRetry={() => dispatch(fetchNewsletters())} />;

  const filtered = items.filter((n) => tab === 'all' || n.status === tab);

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
        <Typography variant="h1">Newsletters</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          New Newsletter
        </Button>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab value="all" label={`All (${items.length})`} />
        <Tab value="draft" label={`Drafts (${items.filter((n) => n.status === 'draft').length})`} />
        <Tab value="published" label={`Published (${items.filter((n) => n.status === 'published').length})`} />
      </Tabs>

      {filtered.length === 0 ? (
        <EmptyState title="No newsletters here" description="Try a different filter, or create a new issue." />
      ) : (
        <Stack spacing={2}>
          {filtered.map((newsletter) => (
            <NewsletterCard key={newsletter.id} newsletter={newsletter} onDelete={requestDelete} />
          ))}
        </Stack>
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete newsletter?"
        message={`"${pendingDelete?.title}" and its action items will be permanently removed.`}
        confirmLabel="Delete"
        destructive
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />
    </Stack>
  );
}
